import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import modules from 'Common/constants/modules';
import ModuleTiles from 'Employee/components/View/ModuleTiles';
import EmployeeTile from 'Employee/components/View/EmployeeTile';
import useQueryParameter from 'Common/hooks/useQueryParameter';
import EmployeeViewTabs from 'Employee/components/View/EmployeeViewTabs';
import EmployeeViewStats from 'Employee/components/View/EmployeeViewStats';
import isAuthorized from 'User/utils/isAuthorized';
import roles from 'User/constants/roles';
import colors from 'Common/constants/colors';
import OuterBox from 'Common/components/Boxes/OuterBox';
import { CloudDownloadRounded } from '@material-ui/icons';

const Layout = styled.div`
    display: flex;
    flex-direction: column;

    @media screen and (min-width: 940px) {
        flex-direction: row;
    }
`;

const Sidebar = styled.div`
    display: flex;
    flex-direction: column;

    > * {
        margin-bottom: 10px;
    }

    @media screen and (min-width: 940px) {
        display: block;
        position: sticky;
        top: 80px;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        max-height: calc(100vh - 150px);
        width: 300px;
        margin-right: 10px;

        > * {
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
`;

const Content = styled.div`
    margin-bottom: 10px;

    @media screen and (min-width: 940px) {
        flex: 1;
        margin-bottom: 0;
    }
`;

const IconWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    width: 28px;
    height: 28px;
    color: ${colors.DIRTY_WHITE};

    &::after {
        content: '';
        display: block;
        width: 1px;
        height: 20px;
        background: ${colors.LIGHT_GRAY};
        position: absolute;
        top: 27px;
    }
`;

const DocumentName = styled.div`
    color: ${colors.DIRTY_WHITE};
    font-size: 14px;
    line-height: 21px;
`;

const DocumentItem = styled(OuterBox).attrs({
    as: 'a',
})`
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding: 20px;
    text-decoration: none;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    cursor: pointer;

    &:last-child {
        ${IconWrapper} {
            &::after {
                display: none;
            }
        }
    }

    &:hover {
        ${DocumentName} {
            color: ${colors.PRIMARY};
        }

        ${IconWrapper} {
            border-color: ${colors.PRIMARY};
        }
    }
`;

const defaultModule = modules.INSTRUCTIONS;

const EmployeeView = ({ employee }) => {
    const [module, setModule] = useQueryParameter('module');

    const handleModuleChange = newModule => {
        if (newModule === defaultModule) {
            setModule(null);
        } else {
            setModule(newModule);
        }
    };

    const selectedModule = module ?? defaultModule;

    const areaInstructionCertificateLink = isAuthorized([roles.MANAGER])
        ? `/api/education/instruction-assignments/certificates/area/download/${employee.id}`
        : '/api/education/instruction-assignments/certificates/area/download';

    return (
        <Layout>
            <Sidebar>
                <EmployeeTile employee={employee} />
                <ModuleTiles
                    selectedModule={selectedModule}
                    onModuleTileClick={handleModuleChange}
                    employee={employee}
                />
                <EmployeeViewStats
                    employee={employee}
                    selectedModule={selectedModule}
                    onModuleChange={handleModuleChange}
                />
                {employee.completedInstructions.length > 0 && (
                    <DocumentItem href={areaInstructionCertificateLink}>
                        <IconWrapper>
                            <CloudDownloadRounded />
                        </IconWrapper>
                        <DocumentName>Bereichszertifikat</DocumentName>
                    </DocumentItem>
                )}
            </Sidebar>
            <Content>
                <EmployeeViewTabs
                    selectedModule={selectedModule}
                    employee={employee}
                />
            </Content>
        </Layout>
    );
};

EmployeeView.defaultProps = {
    employee: null,
};

EmployeeView.propTypes = {
    employee: PropTypes.object,
};

export default EmployeeView;
